import { Box, Grid, Typography } from '@mui/material'
import { Field, FieldProps } from 'formik'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { VinciInput } from '~/components'
import { useResourceAutocomplete } from '~/components/ResourceAutocomplete/ResourceAutocomplete'
import { useFormikUtils } from '~/hooks/useFormikUtils'
import { CompanyForm } from '~/modules/company/components/CompanyForm'
import { createCompany } from '~/modules/company/data/companyApi'
import { ICompany, initialCompany } from '~/utils/types'

export interface ICompanyAssetFormProps {
  singleTitle?: string
}
export const CompanyAssetForm: FC<ICompanyAssetFormProps> = ({ singleTitle }) => {
  const { getInputErrorProps } = useFormikUtils()

  const resourceAutocomplete = useResourceAutocomplete()

  const { t } = useTranslation()
  const getCompanyName = (company?: ICompany | { company?: ICompany } | null) => {
    const parsedCompany = company as (ICompany & { company?: ICompany }) | null | undefined
    return parsedCompany?.name ?? parsedCompany?.company?.name ?? ''
  }

  return (
    <Grid
      container
      justifyContent={'center'}
      alignItems='center'
      sx={{ width: '100%', maxWidth: 600 }}
    >
      <Grid item xs={12} px={1} py={1}>
        <Typography variant='overline'>{t(singleTitle ?? 'Company Identification')}</Typography>
      </Grid>

      <Grid item xs={12} px={1} py={1}>
        <Field name='department_area'>
          {(props: FieldProps) => (
            <VinciInput
              label={t('Department') + '/' + t('Area')}
              {...props.field}
              {...getInputErrorProps('department_area')}
              sx={{ mb: 1 }}
              fullWidth
              onChange={(e) => {
                e.target.value = e.target.value.toUpperCase()

                props.form.handleChange(e)
              }}
            />
          )}
        </Field>
      </Grid>

      <Grid item xs={12} px={1} py={1}>
        <Field name='company_plant'>
          {(props: FieldProps) => (
            <VinciInput
              label={t('Company Plant')}
              {...props.field}
              {...getInputErrorProps('department_area')}
              sx={{ mb: 1 }}
              fullWidth
              onChange={(e) => {
                e.target.value = e.target.value.toUpperCase()

                props.form.handleChange(e)
              }}
            />
          )}
        </Field>
      </Grid>

      <Grid item xs={12} sm={6} px={1} py={1}>
        <Field name='address_line'>
          {(props: FieldProps) => (
            <VinciInput
              label={t('Address Line')}
              {...props.field}
              {...getInputErrorProps('address_line')}
              sx={{ mb: 1 }}
              fullWidth
              onChange={(e) => {
                e.target.value = e.target.value.toUpperCase()

                props.form.handleChange(e)
              }}
            />
          )}
        </Field>
      </Grid>

      <Grid item xs={12} sm={6} px={1} py={1}>
        <Field name='zip'>
          {(props: FieldProps) => (
            <VinciInput
              label={t('ZIP Code')}
              {...props.field}
              {...getInputErrorProps('zip')}
              sx={{ mb: 1 }}
              fullWidth
              onChange={(e) => {
                e.target.value = e.target.value.toUpperCase()

                props.form.handleChange(e)
              }}
            />
          )}
        </Field>
      </Grid>

      <Grid item xs={12} px={1} py={1}>
        {/* <Field name='company_name'>
          {(props: FieldProps) => (
            <VinciInput
              label={t('Company Name')}
              {...props.field}
              {...getInputErrorProps('company_name')}
              sx={{ mb: 1 }}
              fullWidth
              onChange={(e) => {
                e.target.value = e.target.value.toUpperCase()

                props.form.handleChange(e)
              }}
            />
          )}
        </Field> */}
        <Field name='company_name'>
          {(props: FieldProps) =>
            resourceAutocomplete<ICompany>({
              resourcePath: 'company',
              requestProps: {
                initialText: t('Creating company...'),
                errorText: t('There was an error creating company. Please try again!'),
                successText: t('Company successfully created!'),
                createRequestFunction: createCompany,
                successCallback: (response: ICompany) => {
                  props.form.setFieldValue('company_name', response)
                },
              },
              resourceFilter: (item: ICompany) =>
                item.company_type.some((ctype: string) => ctype !== 'asset_manager'),
              renderOption(props, option: ICompany) {
                return (
                  <Box component='li' {...props}>
                    <Typography my={0}>{option.name}</Typography>
                  </Box>
                )
              },
              onSelectResource(item) {
                props.form.setFieldValue('company_name', getCompanyName(item))
              },
              modalProps: {
                form: {
                  initialValues: initialCompany(),
                },
                title: t('Create Company'),
                children: (
                  <Box sx={{ minWidth: { xs: '80vw', md: 700 } }}>
                    <CompanyForm />
                  </Box>
                ),
              },
              autocompleteProps: {
                sx: {
                  flex: 0.2,
                  '& .MuiAutocomplete-inputRoot': {
                    padding: 1,
                  },
                },
                optionLabelGetter(option: ICompany) {
                  if (!option) return ''
                  if (typeof option === 'string') return option
                  return option.name
                },
                primaryIdentifier: 'name',
                label: t('Company Name'),
                error: Boolean(props.form.errors.company_name as string),
                helperText: props.form.errors.company_name as string,
                ...props,
              },
            })
          }
        </Field>
      </Grid>
    </Grid>
  )
}
